// /src/components/IncomingOrders/AddressEditDialog.jsx

import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { getAddressObjectName } from "./helpers/addressUtils";

const AddressEditDialog = ({ address, addressType, getAddressLabel, onClose, onSave, open }) => {
  const intl = useIntl();

  const originalAddressRef = useRef(address);

  const [editDraft, setEditDraft] = useState(() => ({ ...address }));
  const [editType, setEditType] = useState(addressType);  // addressType: "delivery" | "invoice" | "orderer"

  const isFieldDirty = (field) => {
    const originalValue = originalAddressRef.current?.[field] ?? "";
    const draftValue = editDraft?.[field] ?? "";
    return originalValue !== draftValue;
  };

  const isDirty = useMemo(() => (
    editDraft
      ? Object.keys(editDraft).some((field) => isFieldDirty(field))
      : false
  ), [editDraft]);

  const createFieldLabel = (field) =>
    getAddressLabel(getAddressObjectName(addressType), field);

  const dirtyClass = (field) =>
    isFieldDirty(field) ? "dirtyField" : undefined;

  const editTitleByType = (type) => {
    if (type === "delivery") return intl.formatMessage({ id: "orderHistory.deliveryAddress", defaultMessage: "Leveransadress" });
    if (type === "invoice") return intl.formatMessage({ id: "orderHistory.invoiceAddress", defaultMessage: "Fakturaadress" });
    if (type === "orderer") return intl.formatMessage({ id: "orderHistory.ordererAddress", defaultMessage: "Best�llaradress" });
    return "adressuppgift";
  };

  const handleSave = () => {
    onSave?.(editType, editDraft);
  }

  const updateDraftField = (field, value) =>
    setEditDraft((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    originalAddressRef.current = address;
    setEditDraft({ ...address });
    setEditType(addressType);
  }, [address, addressType]);

  const renderField = (field, extraProps = {}) => (
    <TextField
      className={dirtyClass(field)}
      label={createFieldLabel(field)}
      value={editDraft[field] || ""}
      onChange={(e) => updateDraftField(field, e.target.value)}
      fullWidth
      size="small"
      {...extraProps}
    />
  );

  if (!editDraft || !editType)
    return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <span>
          {intl.formatMessage({ id: "btn.edit", defaultMessage: "Redigera" })}{" "}
          {editTitleByType(editType)}
        </span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.2} sx={{
          pt: 1,
          "& .dirtyField .MuiInputBase-root": {
            backgroundColor: "rgba(255, 193, 7, 0.12)",
            boxShadow: "inset 3px 0 0 #f7bd02",
            transition: "background-color 150ms ease",
          }
        }}>
          {renderField("address1")}
          {renderField("address2")}
          {renderField("address3")}
          {renderField("address4")}
          {renderField("address5")}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {renderField("zip")}
            {renderField("city")}
          </Stack>

          {renderField("country")}
          {renderField("reference")}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {renderField("email")}
            {renderField("telephone")}
          </Stack>

          {renderField("contact")}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: "btn.cancel", defaultMessage: "Avbryt" })}
        </Button>
        <Button
          variant="contained"
          disabled={!isDirty}
          onClick={handleSave}
        >
          {intl.formatMessage({ id: "btn.save", defaultMessage: "Spara" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddressEditDialog.displayName = "AddressEditDialog";

export default AddressEditDialog;
